import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePermissionsStore } from '@/stores/permissions';
import { ProjectenMaken } from '@/components/projectmap/ProjectenMaken';
import { getLocalDateString } from '@/stores/tasks';
import { useAdministratieStore, isOpenRow } from '@/stores/administratie';
import { downloadWerfDocumenten, type AdministratieRow } from '@/api/administratie';
import { AdministratieTable, AdministratieCards, type RowActions } from '@/components/administratie/AdministratieList';
import { LimosaDialog, type LimosaDialogPerson } from '@/components/administratie/LimosaDialog';
import { PersoonsdocumentenDialog, type DocumentenPerson } from '@/components/administratie/PersoonsdocumentenDialog';
import { groupByWerf, werfHasPeople, type WerfGroup } from '@/components/administratie/groupByWerf';

const dateFormatter = new Intl.DateTimeFormat('nl-BE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

function matchesSearch(row: AdministratieRow, term: string): boolean {
  const lower = term.toLowerCase();
  return [row.projectName, row.taskName, row.klant, row.werfAdres, row.installerName, row.checkinWorkNr]
    .some((v) => typeof v === 'string' && v.toLowerCase().includes(lower));
}

const PROJECTEN_MAKEN = 'projecten-maken';

/**
 * Administratie: the day overview and, for whoever holds the
 * `projecten_maken` feature, the "Projecten maken" tab. The tab lives in the
 * URL (`?tab=projecten-maken`) so it survives a refresh.
 */
export function AdministratieView() {
  const canProjectenMaken = usePermissionsStore((st) => st.canFeature('projecten_maken'));
  const [params, setParams] = useSearchParams();
  const tab = canProjectenMaken && params.get('tab') === PROJECTEN_MAKEN ? PROJECTEN_MAKEN : 'dag';

  if (!canProjectenMaken) return <AdministratieDag />;

  const tabClass = (active: boolean) =>
    `px-3 py-1.5 text-sm font-semibold rounded-full transition ${active ? 'bg-td-blue text-white' : 'text-td-navy hover:bg-td-blue/10'}`;
  return (
    <div>
      <div role="tablist" aria-label="Administratie" className="px-4 lg:px-8 pt-4 lg:pt-6 flex gap-2">
        <button type="button" role="tab" aria-selected={tab === 'dag'} className={tabClass(tab === 'dag')} onClick={() => setParams({})}>
          Dagoverzicht
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === PROJECTEN_MAKEN}
          className={tabClass(tab === PROJECTEN_MAKEN)}
          onClick={() => setParams({ tab: PROJECTEN_MAKEN })}
        >
          Projecten maken
        </button>
      </div>
      {tab === PROJECTEN_MAKEN ? <ProjectenMaken /> : <AdministratieDag />}
    </div>
  );
}

/**
 * One day of tasks, one row per task × installer, with meldingsmail, check-in
 * and Limosa — set inline, without opening Odoo.
 */
function AdministratieDag() {
  const s = useAdministratieStore();
  const [limosa, setLimosa] = useState<{ row: AdministratieRow; person?: LimosaDialogPerson } | null>(null);
  const [documentenPerson, setDocumentenPerson] = useState<DocumentenPerson | null>(null);
  const { fetchDay, selectedDate } = s;
  const [documentenBusy, setDocumentenBusy] = useState<string | null>(null);
  const [documentenError, setDocumentenError] = useState<string | null>(null);

  useEffect(() => {
    fetchDay();
    setDocumentenError(null);
  }, [fetchDay, selectedDate]);

  const visible = useMemo(() => {
    let rows = s.rows;
    if (s.onlyOpen) rows = rows.filter((r) => isOpenRow(r));
    const term = s.searchTerm.trim();
    if (term) rows = rows.filter((r) => matchesSearch(r, term));
    return rows;
  }, [s.rows, s.onlyOpen, s.searchTerm]);

  const groups = useMemo(() => groupByWerf(visible), [visible]);
  // Presence on the whole day: a filter that hides a werf's present rows must not hide its documents.
  const werfWithPeople = useMemo(
    () => new Set(groupByWerf(s.rows).filter(werfHasPeople).map((g) => g.key)),
    [s.rows],
  );
  const openCount = useMemo(() => s.rows.filter((r) => isOpenRow(r)).length, [s.rows]);
  const isToday = selectedDate === getLocalDateString();
  const formattedDate = dateFormatter.format(new Date(selectedDate + 'T12:00:00'));

  const actions: RowActions = {
    busyKeys: s.busyKeys,
    onGemeld: (row, value) => void s.toggleGemeld(row, value),
    onIngecheckt: (row, value) => void s.toggleIngecheckt(row, value),
    onLimosa: (row, person) => setLimosa({ row, person }),
    onDocumenten: setDocumentenPerson,
    onCheckinWork: (projectId, value) => void s.saveCheckinWork(projectId, value),
    onAanwezig: (row, workerId, value) => void s.toggleAanwezig(row, workerId, value),
    onWorkerIngecheckt: (row, workerId, value) => void s.toggleWorkerIngecheckt(row, workerId, value),
    documenten: {
      busyKey: documentenBusy,
      hasPeople: (group) => werfWithPeople.has(group.key),
      onDownload: (group) => void downloadDocumenten(group),
    },
  };

  async function downloadDocumenten(group: WerfGroup) {
    if (group.projectId == null || documentenBusy) return;
    setDocumentenBusy(group.key);
    setDocumentenError(null);
    try {
      await downloadWerfDocumenten(group.projectId, selectedDate);
    } catch (err) {
      setDocumentenError(err instanceof Error && err.message ? err.message : 'Documenten ophalen mislukt');
    } finally {
      setDocumentenBusy(null);
    }
  }

  return (
    <div>
      <div className="px-4 lg:px-8 pt-4 lg:pt-6 pb-2 lg:pb-4">
        <div className="grid grid-cols-[2.25rem_1fr_2.25rem] items-start gap-2 w-full max-w-md mx-auto">
          <button type="button" onClick={s.prevDay} aria-label="Vorige dag" className="flex items-center justify-center w-9 h-9 rounded-full text-td-blue hover:bg-td-blue/10 transition">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <h1 className="py-1 text-lg leading-7 lg:text-xl font-extrabold text-td-navy capitalize text-center">{formattedDate}</h1>
          <button type="button" onClick={s.nextDay} aria-label="Volgende dag" className="flex items-center justify-center w-9 h-9 rounded-full text-td-blue hover:bg-td-blue/10 transition">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          </button>
        </div>
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 h-6 mt-1">
          <span aria-hidden="true" />
          <p className="text-td-muted text-xs lg:text-sm">{visible.length} {visible.length === 1 ? 'rij' : 'rijen'} · {openCount} open</p>
          {!isToday ? (
            <button type="button" onClick={s.goToToday} className="justify-self-start px-3 py-0.5 text-xs font-semibold bg-td-blue text-white rounded-full hover:bg-td-blue/90 transition">
              Vandaag
            </button>
          ) : <span aria-hidden="true" />}
        </div>
      </div>

      <div className="px-4 lg:px-8 flex flex-col sm:flex-row sm:items-center gap-3">
        <label htmlFor="administratie-search" className="sr-only">Zoeken</label>
        <input
          id="administratie-search"
          type="text"
          placeholder="Zoek project, klant, werf of installateur..."
          value={s.searchTerm}
          onChange={(e) => s.setSearchTerm(e.target.value)}
          className="w-full sm:max-w-sm px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-td-blue focus:border-transparent"
        />
        <label className="flex items-center gap-2 text-sm text-td-navy">
          <input type="checkbox" checked={s.onlyOpen} onChange={(e) => s.setOnlyOpen(e.target.checked)} className="w-4 h-4" />
          Alleen open punten
        </label>
      </div>

      <div className="px-4 lg:px-8 pb-8 mt-3 lg:mt-5 space-y-3">
        {s.saveError && (
          <div className="flex items-start justify-between gap-3 bg-red-50 text-red-700 p-3 rounded-xl text-sm" role="alert">
            <span>{s.saveError} — de wijziging is teruggezet.</span>
            <button type="button" onClick={s.clearSaveError} aria-label="Melding sluiten" className="font-bold">×</button>
          </div>
        )}
        {documentenError && (
          <div className="flex items-start justify-between gap-3 bg-red-50 text-red-700 p-3 rounded-xl text-sm" role="alert">
            <span>{documentenError}</span>
            <button type="button" onClick={() => setDocumentenError(null)} aria-label="Melding sluiten" className="font-bold">×</button>
          </div>
        )}
        {s.truncated && (
          <p className="bg-amber-50 text-amber-800 p-3 rounded-xl text-sm">
            Deze dag heeft meer dan 100 taken; niet alle taken worden getoond.
          </p>
        )}
        {s.loading && <p className="text-center text-td-muted text-sm py-12" role="status">Taken laden...</p>}
        {s.error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-xl" role="alert">
            <p className="font-semibold">Fout bij laden</p>
            <p className="text-sm mt-1">{s.error}</p>
          </div>
        )}
        {!s.loading && !s.error && visible.length === 0 && (
          <p className="text-center text-td-muted text-sm py-12">
            {s.onlyOpen && s.rows.length > 0 ? 'Geen open punten voor deze dag' : 'Geen taken gevonden'}
          </p>
        )}
        {!s.loading && !s.error && visible.length > 0 && (
          <>
            <div className="hidden lg:block"><AdministratieTable groups={groups} actions={actions} /></div>
            <div className="lg:hidden"><AdministratieCards groups={groups} actions={actions} /></div>
          </>
        )}
      </div>

      {limosa && <LimosaDialog row={limosa.row} person={limosa.person} onClose={() => setLimosa(null)} onChanged={() => void fetchDay()} />}
      {documentenPerson && <PersoonsdocumentenDialog person={documentenPerson} onClose={() => setDocumentenPerson(null)} />}
    </div>
  );
}
